// @/components/layout/main.jsx
// Main layout with dynamic sidebar architecture
// Sidebar collapses to icon-only mode, never fully disappears

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { getTheme, LAYOUT_DEFAULT, LAYOUT_WIDGET } from "@/config/sidebar-theme";

// ============================================================================
// CONSTANTS
// ============================================================================

const SIDEBAR_EXPANDED_WIDTH = 320;
const SIDEBAR_COLLAPSED_WIDTH = 84;
const BREAKPOINT_TABLET = 1280;
const BREAKPOINT_MOBILE = 768;
const PAGE_ENTRY_SIDEBAR_OFFSET = 12;

// Custom event names for sidebar control
const SIDEBAR_EXPAND_EVENT = "sidebar:expand";
const SIDEBAR_COLLAPSE_EVENT = "sidebar:collapse";

// Snappy spring config
const springConfig = {
  type: "spring",
  damping: 25,
  stiffness: 300,
  mass: 0.5,
};

// Softer spring for initial load
const entrySpringConfig = {
  type: "spring",
  damping: 28,
  stiffness: 280,
  mass: 0.6,
};

// ============================================================================
// CONTEXT
// ============================================================================

const LayoutContext = createContext(null);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (context) return context;
  // During Gatsby's static build there is no LayoutMain ancestor (providers are
  // wired via wrapPageElement in gatsby-browser.js only; there is no
  // gatsby-ssr.js). Authed pages SSR to a throwaway shell the client re-renders,
  // so return inert defaults instead of crashing the build. On the client a
  // missing provider is still a real bug, so keep throwing there.
  if (typeof window === "undefined") {
    return {
      isExpanded: true,
      isMobile: false,
      isMobileMenuOpen: false,
      layout: LAYOUT_DEFAULT,
      toggleSidebar: () => {},
      expandSidebar: () => {},
      collapseSidebar: () => {},
      openMobileMenu: () => {},
      closeMobileMenu: () => {},
      setRightPanel: () => {},
    };
  }
  throw new Error("useLayout must be used within a LayoutMain");
};

// ============================================================================
// SIDEBAR CONTROL UTILITIES
// ============================================================================

/**
 * Expand the sidebar from anywhere in the app
 * Dispatches a custom event that LayoutMain listens for
 */
export const expandSidebarGlobal = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SIDEBAR_EXPAND_EVENT));
  }
};

/**
 * Collapse the sidebar from anywhere in the app
 * Dispatches a custom event that LayoutMain listens for
 */
export const collapseSidebarGlobal = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SIDEBAR_COLLAPSE_EVENT));
  }
};

// ============================================================================
// LAYOUT MAIN
// ============================================================================

const LayoutMain = ({ children, defaultExpanded = true, layout = LAYOUT_DEFAULT }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [wasAutoCollapsed, setWasAutoCollapsed] = useState(false);

  const theme = getTheme(layout);

  // Handle responsive breakpoints
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      const width = window.innerWidth;

      if (width < BREAKPOINT_MOBILE) {
        setIsMobile(true);
        setIsExpanded(false);
        setWasAutoCollapsed(true);
      } else if (width < BREAKPOINT_TABLET) {
        setIsMobile(false);
        setIsExpanded(false);
        setWasAutoCollapsed(true);
      } else {
        setIsMobile(false);
        if (wasAutoCollapsed) {
          setIsExpanded(true);
          setWasAutoCollapsed(false);
        }
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [wasAutoCollapsed]);

  // Listen for global sidebar expand/collapse events
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleExpand = () => setIsExpanded(true);
    const handleCollapse = () => setIsExpanded(false);

    window.addEventListener(SIDEBAR_EXPAND_EVENT, handleExpand);
    window.addEventListener(SIDEBAR_COLLAPSE_EVENT, handleCollapse);

    return () => {
      window.removeEventListener(SIDEBAR_EXPAND_EVENT, handleExpand);
      window.removeEventListener(SIDEBAR_COLLAPSE_EVENT, handleCollapse);
    };
  }, []);

  const toggleSidebar = useCallback(() => setIsExpanded((prev) => !prev), []);
  const expandSidebar = useCallback(() => setIsExpanded(true), []);
  const collapseSidebar = useCallback(() => setIsExpanded(false), []);
  const openMobileMenu = useCallback(() => setIsMobileMenuOpen(true), []);
  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  const [rightPanel, setRightPanel] = useState(null);

  // A right panel (the reduction coach) competes with the sidebar for the same
  // horizontal space, and the page content between them is what gets crushed —
  // headings wrap mid-word and cards clip instead of reflowing. So opening a
  // right panel collapses the nav, exactly like the tablet breakpoint does, and
  // closing it restores the nav only if we were the ones who collapsed it.
  // Reacts to the panel appearing/disappearing, not to `isExpanded`, so a manual
  // toggle while the panel is open is left alone.
  const isExpandedRef = useRef(isExpanded);
  useEffect(() => { isExpandedRef.current = isExpanded; });
  const navAutoCollapsedRef = useRef(false);
  useEffect(() => {
    if (rightPanel) {
      if (isExpandedRef.current) {
        navAutoCollapsedRef.current = true;
        setIsExpanded(false);
      }
    } else if (navAutoCollapsedRef.current) {
      navAutoCollapsedRef.current = false;
      setIsExpanded(true);
    }
  }, [rightPanel]);

  const contextValue = useMemo(
    () => ({
      isExpanded,
      isMobile,
      isMobileMenuOpen,
      layout,
      toggleSidebar,
      expandSidebar,
      collapseSidebar,
      openMobileMenu,
      closeMobileMenu,
      setRightPanel,
    }),
    [isExpanded, isMobile, isMobileMenuOpen, layout, toggleSidebar, expandSidebar, collapseSidebar, openMobileMenu, closeMobileMenu, setRightPanel]
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      <div className={`s-platform h-dvh overflow-hidden ${theme.container.outer}`}>
        <div className={`flex h-full ${theme.container.inner}`}>
          <motion.div
            className={`flex flex-1 overflow-hidden ${theme.container.card}`}
            initial={false}
            animate={{ opacity: 1 }}
            transition={entrySpringConfig}
          >
            {children}
            <AnimatePresence>{rightPanel}</AnimatePresence>
          </motion.div>
        </div>
      </div>
    </LayoutContext.Provider>
  );
};

// ============================================================================
// LAYOUT SIDEBAR
// ============================================================================

export const LayoutSidebar = ({ children, className = "" }) => {
  const { isExpanded, isMobile, layout, expandSidebar } = useLayout();
  const [hasAnimatedEntry, setHasAnimatedEntry] = useState(false);

  const theme = getTheme(layout);

  useEffect(() => {
    const timer = setTimeout(() => setHasAnimatedEntry(true), 400);
    return () => clearTimeout(timer);
  }, []);

  const targetWidth = isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

  const handleSidebarClick = useCallback(
    (e) => {
      if (!isExpanded) {
        const target = e.target;
        const isInteractive =
          target.closest("a") ||
          target.closest("button") ||
          target.closest('[role="button"]') ||
          target.closest('[data-interactive="true"]');

        if (!isInteractive) {
          expandSidebar();
        }
      }
    },
    [isExpanded, expandSidebar]
  );

  if (isMobile) return null;

  return (
    <motion.aside
      className={`shrink-0 overflow-hidden ${theme.sidebar} ${!isExpanded ? "cursor-pointer" : ""} ${className}`}
      data-layout={layout}
      initial={{ width: SIDEBAR_EXPANDED_WIDTH - PAGE_ENTRY_SIDEBAR_OFFSET, opacity: 0.4 }}
      animate={{ width: targetWidth, opacity: 1 }}
      transition={hasAnimatedEntry ? springConfig : entrySpringConfig}
      onClick={handleSidebarClick}
    >
      <motion.div
        className="h-full overflow-hidden"
        initial={{ width: SIDEBAR_EXPANDED_WIDTH - PAGE_ENTRY_SIDEBAR_OFFSET }}
        animate={{ width: targetWidth }}
        transition={hasAnimatedEntry ? springConfig : entrySpringConfig}
      >
        {children}
      </motion.div>
    </motion.aside>
  );
};

// ============================================================================
// LAYOUT CONTENT
// ============================================================================

export const LayoutContent = ({ children, className = "" }) => (
  <main className={`flex-1 min-w-0 flex flex-col overflow-auto ${className}`}>
    {children}
  </main>
);

// ============================================================================
// LAYOUT HEADER & BODY
// ============================================================================

export const LayoutHeader = ({ children, className = "" }) => (
  <header className={`shrink-0 ${className}`}>{children}</header>
);

export const LayoutBody = ({ children, className = "" }) => (
  <div className={`flex-1 overflow-auto ${className}`}>{children}</div>
);

// ============================================================================
// EXPORTS
// ============================================================================

export {
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  BREAKPOINT_TABLET,
  BREAKPOINT_MOBILE,
  LAYOUT_DEFAULT,
  LAYOUT_WIDGET,
  springConfig,
};

export default LayoutMain;
